import * as THREE from 'three';

/**
 * How each style draws: blending, opacity along the hilt and tip edges, the share of its
 * length that burns white near the weapon, and whether the tail narrows to the tip.
 */
export const TRAIL_STYLES = {
  ribbon: { additive: false, hilt: 0.85, tip: 0.85, hot: 0, taper: false },
  sweep: { additive: true, hilt: 0, tip: 1, hot: 0, taper: true },
  glow: { additive: true, hilt: 0.5, tip: 1, hot: 0.5, taper: false },
};
export type TrailStyle = keyof typeof TRAIL_STYLES;
const MAX_SAMPLES = 128;
const WHITE = new THREE.Color(0xffffff);
const ORIGIN = new THREE.Vector3();

interface Sample {
  hilt: THREE.Vector3;
  tip: THREE.Vector3;
  age: number;
}

/** A world-space ribbon swept by a weapon's edge, fading out over `seconds`. */
export class WeaponTrail {
  readonly mesh: THREE.Mesh;
  private samples: Sample[] = [];
  private positions = new Float32Array(MAX_SAMPLES * 6);
  private colors = new Float32Array(MAX_SAMPLES * 8);
  private color: THREE.Color;
  private shade = new THREE.Color();
  private inner = new THREE.Vector3();
  private outer = new THREE.Vector3();

  constructor(
    color: THREE.ColorRepresentation,
    private style: TrailStyle,
    private seconds: number,
  ) {
    this.color = new THREE.Color(color);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(this.positions, 3).setUsage(
        THREE.DynamicDrawUsage,
      ),
    );
    geometry.setAttribute(
      'color',
      new THREE.BufferAttribute(this.colors, 4).setUsage(
        THREE.DynamicDrawUsage,
      ),
    );
    const index: number[] = [];
    for (let i = 0; i < MAX_SAMPLES - 1; i++) {
      const a = i * 2;
      index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    geometry.setIndex(index);
    geometry.setDrawRange(0, 0);
    this.mesh = new THREE.Mesh(
      geometry,
      new THREE.MeshBasicMaterial({
        vertexColors: true,
        transparent: true,
        toneMapped: false,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: TRAIL_STYLES[style].additive
          ? THREE.AdditiveBlending
          : THREE.NormalBlending,
      }),
    );
    this.mesh.frustumCulled = false;
  }

  /**
   * Ages the trail and extends it to the weapon's edge (`UnitView.weaponEdge`), if it has one.
   * `origin` is where the mesh's parent sits; the parent may move but not turn or scale.
   */
  update(
    dt: number,
    edge: [THREE.Vector3, THREE.Vector3] | null,
    origin = ORIGIN,
  ): void {
    if (dt <= 0) return;
    for (const sample of this.samples) sample.age += dt;
    while (this.samples.length && this.samples.at(-1)!.age > this.seconds)
      this.samples.pop();
    if (edge) this.samples.unshift({ hilt: edge[0], tip: edge[1], age: 0 });
    if (this.samples.length > MAX_SAMPLES) this.samples.length = MAX_SAMPLES;

    const { hilt, tip, hot, taper } = TRAIL_STYLES[this.style];
    this.samples.forEach((sample, i) => {
      const t = sample.age / this.seconds;
      const inner = this.inner.copy(sample.hilt);
      if (taper) inner.lerp(sample.tip, t);
      inner.sub(origin).toArray(this.positions, i * 6);
      this.outer
        .copy(sample.tip)
        .sub(origin)
        .toArray(this.positions, i * 6 + 3);
      this.shade
        .copy(this.color)
        .lerp(WHITE, hot > 0 ? Math.max(0, 1 - t / hot) : 0);
      const { r, g, b } = this.shade;
      this.colors.set([r, g, b, hilt * (1 - t), r, g, b, tip * (1 - t)], i * 8);
    });
    const geometry = this.mesh.geometry;
    geometry.attributes.position.needsUpdate = true;
    geometry.attributes.color.needsUpdate = true;
    geometry.setDrawRange(0, Math.max(0, this.samples.length - 1) * 6);
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
